import { FC } from "react";
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/solid";
import { Box, Tooltip } from "@mui/material";
import { ReportEntry } from "../../../@types/ReportEntry";
import Collaborator from "../../../@types/Collaborator";
import {
  canApproveEntry,
  canApprovePerManager,
  canRepprove,
  canEditReportEntry,
  canReturnToApproval,
  canMarkAsPaid,
  canMarkAsPaidForAdvance,
  canConfirmAdvanceReceipt,
} from "../../../utils/reportStatusUtils";

interface ReportEntryActionsProps {
  data: ReportEntry;
  loggedUser?: Collaborator;
  onApprove: () => void;
  onApprovePerManager: () => void;
  onRefuse: () => void;
  onReturnToApproval: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMarkAsPaid?: () => void;
  onConfirmReceipt?: () => void;
}

const ReportEntryActions: FC<ReportEntryActionsProps> = ({
  data,
  loggedUser,
  onApprove,
  onApprovePerManager,
  onRefuse,
  onReturnToApproval,
  onEdit,
  onDelete,
  onMarkAsPaid,
  onConfirmReceipt,
}) => {
  return (
    <Box className="flex flex-row items-center justify-end gap-3">
      {canApproveEntry(data, loggedUser) && (
        <Tooltip title={"Aprovar"}>
          <CheckCircleIcon
            onClick={onApprove}
            className="w-6 h-6 text-green-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {canApprovePerManager(data, loggedUser) && (
        <Tooltip title={"Aprovar pelo Gestor"}>
          <ClipboardDocumentCheckIcon
            onClick={onApprovePerManager}
            className="w-6 h-6 text-green-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {canRepprove(loggedUser, data) && (
        <Tooltip title={"Rejeitar"}>
          <XCircleIcon
            onClick={onRefuse}
            className="w-6 h-6 text-red-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {canReturnToApproval(loggedUser, data) && (
        <Tooltip title={"Retornar para Aprovação"}>
          <ArrowUturnLeftIcon
            onClick={onReturnToApproval}
            className="w-5 h-5 text-red-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {(canMarkAsPaid(loggedUser, data) || canMarkAsPaidForAdvance(loggedUser, data)) && onMarkAsPaid && (
        <Tooltip title={"Marcar como Pago"}>
          <CurrencyDollarIcon
            onClick={onMarkAsPaid}
            className="w-6 h-6 text-blue-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {canConfirmAdvanceReceipt(loggedUser, data) && onConfirmReceipt && (
        <Tooltip title={"Confirmar Recebimento"}>
          <DocumentCheckIcon
            onClick={onConfirmReceipt}
            className="w-6 h-6 text-purple-600 cursor-pointer hover:opacity-80 transition-opacity"
          />
        </Tooltip>
      )}

      {canEditReportEntry(data, loggedUser) && (
        <>
          <Tooltip title={"Editar Solicitação"}>
            <PencilSquareIcon
              onClick={onEdit}
              className="w-6 h-6 text-green-600 cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Tooltip>

          <Tooltip title={"Apagar Solicitação"}>
            <TrashIcon
              onClick={onDelete}
              className="w-6 h-6 text-red-600 cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default ReportEntryActions;
